import { Component, OnInit, inject, ViewChild, TemplateRef, HostListener } from '@angular/core';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { SiteSetup } from '../../models/response.model';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { SetupScreensService } from '../../services/setup-screens.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { Alert, AlertService, AlertType, ModalComponent } from '@michelin/theme';
import { FormBuilder, FormGroup, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { hasRolePermission, getCountries, mapCountry } from '../../../user-management/utils/UserSetupUtils';
import { DealerState } from '../../../../shared/state/DealerState';
import { Store } from '@ngxs/store';
import { ROLES } from '../../../authentication/authRoles.constants';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';

@Component({
  selector: 'app-site-setup-details',
  templateUrl: './site-setup-details.component.html',
  styleUrl: './site-setup-details.component.scss',
  standalone: false
})
export class SiteSetupDetailsComponent implements OnInit {
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  productSaveDisabled = true;
  saveEnabled = false;
  siteId: string;
  siteCodes: string[] = [];
  siteDetails: SiteSetup;
  siteDetailsForm: FormGroup;
  countries = getCountries();

  public translationService = inject(TranslationService);
  public setupScreensService = inject(SetupScreensService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public store = inject(Store);
  public fb = inject(FormBuilder);
  public activatedRoute = inject(ActivatedRoute);

  constructor() {
    this.activatedRoute.params.subscribe((params) => {
      this.siteId = params['siteId'];
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.site_edit);
    this.initSiteDetailsForm();
    const sites = this.store.selectSnapshot(DealerState.sites);
    if (sites && sites.length > 0) {
      this.siteCodes = sites.map((site) => site.code);
    }
    if (this.siteId != undefined && this.siteId != 'create') {
      this.setupScreensService.getSiteDetails(this.siteId).subscribe({
        next: (response) => {
          this.siteDetails = response.data;
          this.siteDetailsForm.patchValue(this.siteDetails);
          this.updateToggleValues();
        },
        error: (err) => {
          console.log(err);
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Please try again. Something went wrong.'
          } as Alert);
        }
      });
    }
  }

  private initSiteDetailsForm(): void {
    this.siteDetailsForm = this.fb.group({
      siteSetupPk: [''],
      siteCode: ['', [this.siteCodeUniqueValidator()]],
      siteName: [''],
      addressOne: [''],
      addressTwo: [''],
      city: [''],
      stateProvince: [''],
      postalCode: [''],
      countryCode: [''],
      contactName: [''],
      phoneNumber: [''],
      faxNumber: [''],
      emailAdress: [''],
      siteActive: true,
      interfaceId1: [''],
      interfaceId2: [''],
      shipping: 'N',
      receiving: 'N',
      scrapShipping: false,
      rejectByCasingPost: false,
      rejectByCustomerPost: false
    });
  }

  private siteCodeUniqueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !this.siteCodes || this.siteCodes.length === 0) return null;
      return this.siteCodes.includes(control.value) && this.siteId == 'create' ? { siteCodeNotUnique: true } : null;
    };
  }

  updateToggleValues() {
    this.siteDetailsForm.patchValue({
      countryCode: mapCountry(this.siteDetails.countryCode),
      siteActive: this.siteDetailsForm.get('siteActive').value === 'Y',
      scrapShipping: this.siteDetailsForm.get('scrapShipping').value === 'Y',
      rejectByCasingPost: this.siteDetailsForm.get('rejectByCasingPost').value === 'Y',
      rejectByCustomerPost: this.siteDetailsForm.get('rejectByCustomerPost').value === 'Y'
    });
  }

  checkIfModified(): boolean {
    if (!this.siteDetailsForm) {
      return false;
    }

    // When creating a new site, check if any fields have been filled
    if (this.siteId === 'create' || !this.siteDetails) {
      const formValues = this.siteDetailsForm.value;
      // Check if any field has a value or is different from the default
      return !!(
        formValues.siteCode ||
        formValues.siteName ||
        formValues.addressOne ||
        formValues.addressTwo ||
        formValues.city ||
        formValues.stateProvince ||
        formValues.postalCode ||
        formValues.countryCode ||
        formValues.contactName ||
        formValues.phoneNumber ||
        formValues.faxNumber ||
        formValues.emailAdress ||
        formValues.interfaceId1 ||
        formValues.interfaceId2 ||
        formValues.shipping !== 'N' ||
        formValues.receiving !== 'N' ||
        formValues.scrapShipping === true ||
        formValues.rejectByCasingPost === true ||
        formValues.rejectByCustomerPost === true ||
        formValues.siteActive === false
      );
    }

    // Get current form values with proper transformations
    const countryCodeValue = this.siteDetailsForm.get('countryCode').value;
    const currentValues = {
      ...this.siteDetailsForm.value,
      siteActive: this.siteDetailsForm.get('siteActive').value === true ? 'Y' : 'N',
      scrapShipping: this.siteDetailsForm.get('scrapShipping').value === true ? 'Y' : 'N',
      rejectByCasingPost: this.siteDetailsForm.get('rejectByCasingPost').value === true ? 'Y' : 'N',
      rejectByCustomerPost: this.siteDetailsForm.get('rejectByCustomerPost').value === true ? 'Y' : 'N',
      countryCode: countryCodeValue ? countryCodeValue.split(' ')[0] : ''
    };

    // List of fields to compare
    const fieldsToCompare = [
      'siteCode',
      'siteName',
      'addressOne',
      'addressTwo',
      'city',
      'stateProvince',
      'postalCode',
      'countryCode',
      'contactName',
      'phoneNumber',
      'faxNumber',
      'emailAdress',
      'siteActive',
      'interfaceId1',
      'interfaceId2',
      'shipping',
      'receiving',
      'scrapShipping',
      'rejectByCasingPost',
      'rejectByCustomerPost'
    ];

    // Compare each field with original values
    return fieldsToCompare.some((field) => {
      const currentValue = currentValues[field];
      const originalValue = this.siteDetails[field];
      return currentValue !== originalValue;
    });
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    this.siteDetailsForm.markAllAsTouched();

    // Check form validity first (required fields)
    if (!this.siteDetailsForm.valid) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('missingValuesText')
      } as Alert);
      return;
    }

    // Then check if any modifications were made
    if (!this.checkIfModified()) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
      return;
    }

    const countryCodeValue = this.siteDetailsForm.get('countryCode').value;
    const updatedSiteDetails: SiteSetup = {
      ...this.siteDetailsForm.value,
      siteSetupPk: this.siteId,
      siteActive: this.siteDetailsForm.get('siteActive').value === true ? 'Y' : 'N',
      scrapShipping: this.siteDetailsForm.get('scrapShipping').value === true ? 'Y' : 'N',
      rejectByCasingPost: this.siteDetailsForm.get('rejectByCasingPost').value === true ? 'Y' : 'N',
      rejectByCustomerPost: this.siteDetailsForm.get('rejectByCustomerPost').value === true ? 'Y' : 'N',
      countryCode: countryCodeValue ? countryCodeValue.split(' ')[0] : ''
    };

    this.setupScreensService.updateSite(updatedSiteDetails).subscribe({
      next: (response) => {
        this.siteDetails = response.data;

        // If this was a create, update the siteId to the new ID
        if (this.siteId === 'create' && response.data?.siteSetupPk) {
          this.siteId = response.data.siteSetupPk;
        }

        // Update the form with the saved data to sync state
        this.siteDetailsForm.patchValue(this.siteDetails);
        this.updateToggleValues();

        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.saveEnabled && this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(this.checkIfModified.bind(this), this.discardModal, this.discardContent, this.translationService);
  }
}
